/**
 * Help & Documentation Page
 * Comprehensive knowledge base for the Deal Intelligence Dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Lightbulb,
  GitBranch,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Home,
  Briefcase,
  Calculator,
  Wrench,
  Building2,
  TrendingUp,
  Users,
  Brain,
  Database,
  Target,
  CircleDot,
  ExternalLink,
} from 'lucide-react';

import { HelpSearch, AnnotatedScreenshot, pageAnnotations } from '../components/help';
import {
  pageExplanations,
  keyConcepts,
  investmentWorkflow,
  moduleDeepDives,
  decisionFrameworks,
} from '../content/helpContent';
import { cn } from '../lib/utils';

// ============== SECTION COMPONENTS ==============

const Section = ({ id, title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <section id={id} className="scroll-mt-20">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 text-left group"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="text-primary" size={20} />}
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <ChevronDown 
          className={cn(
            "text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} 
          size={18} 
        />
      </button>
      
      {isOpen && (
        <div className="pb-6 animate-in fade-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </section>
  );
};

const SubSection = ({ title, children, className }) => (
  <div className={cn("mb-6", className)}>
    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
      {title}
    </h3>
    {children}
  </div>
);

const Card = ({ children, className }) => (
  <div className={cn(
    "p-4 rounded-lg border border-border bg-card",
    className
  )}>
    {children}
  </div>
);

const ProseBlock = ({ children }) => (
  <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed">
    {children}
  </div>
);

// ============== SIDEBAR NAVIGATION ==============

const SidebarNav = ({ activeSection, onNavigate }) => {
  const sections = [
    { id: 'overview', title: 'Vue d\'Ensemble', icon: Home },
    { id: 'workflow', title: 'Processus d\'Investissement', icon: GitBranch },
    { id: 'modules', title: 'Modules en Détail', icon: BookOpen },
    { id: 'concepts', title: 'Concepts Clés', icon: Lightbulb },
    { id: 'decisions', title: 'Cadres de Décision', icon: Target },
    { id: 'pages', title: 'Référence Pages', icon: FileText },
  ];

  return (
    <nav className="space-y-1">
      {sections.map((section) => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        
        return (
          <button
            key={section.id}
            onClick={() => onNavigate(section.id)}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left",
              isActive
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon size={16} />
            <span>{section.title}</span>
          </button>
        );
      })}
    </nav>
  );
};

// ============== MAIN HELP PAGE ==============

export const Help = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('overview');
  const contentRef = useRef(null);

  // Handle hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setActiveSection(hash);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const handleNavigate = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSearchResult = (result) => {
    // Navigate to relevant section based on result type
    if (result.type === 'page') {
      handleNavigate('pages');
    } else if (result.type === 'concept') {
      handleNavigate('concepts');
    } else if (result.type === 'module') {
      handleNavigate('modules');
    } else if (result.type === 'framework') {
      handleNavigate('decisions');
    } else if (result.type === 'workflow') {
      handleNavigate('workflow');
    }
    
    // Scroll to specific item after a short delay
    setTimeout(() => {
      const element = document.getElementById(result.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
        }, 2000);
      }
    }, 300);
  };

  return (
    <div className="min-h-screen" data-testid="help-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Aide & Documentation</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Apprenez à prendre des décisions d'investissement basées sur les données
              </p>
            </div>
            <div className="w-80">
              <HelpSearch onResultClick={handleSearchResult} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="col-span-3 sticky top-24 h-fit">
            <SidebarNav activeSection={activeSection} onNavigate={handleNavigate} />
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Liens Rapides</p>
              <div className="space-y-1">
                <Link 
                  to="/" 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Home size={14} />
                  <span>Retour au Tableau de Bord</span>
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main ref={contentRef} className="col-span-9 space-y-8">
            
            {/* ============== SYSTEM OVERVIEW ============== */}
            <Section id="overview" title="Vue d'Ensemble du Système" icon={Home}>
              <div className="space-y-6">
                <Card>
                  <h3 className="font-semibold mb-2">Qu'est-ce que le Dashboard Deal Intelligence ?</h3>
                  <ProseBlock>
                    <p>
                      Une plateforme d'analyse d'investissement complète pour l'acquisition 
                      d'appartements hors-marché à Paris, la rénovation et la revente. Le système 
                      fournit une aide à la décision basée sur les données, du sourcing à la vente.
                    </p>
                  </ProseBlock>
                </Card>

                <SubSection title="Comment les Modules sont Connectés">
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                        <Briefcase className="text-primary" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">Pipeline Deals</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Suivre les deals par étapes
                      </p>
                    </Card>
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-2">
                        <Calculator className="text-emerald-500" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">Analyseur Deal</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Calculer profit & rendement
                      </p>
                    </Card>
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-2">
                        <Building2 className="text-amber-500" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">Comparables</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Valider les prix
                      </p>
                    </Card>
                  </div>
                  <div className="flex items-center justify-center my-4">
                    <ArrowRight className="text-muted-foreground" size={24} />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-2">
                        <Wrench className="text-orange-500" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">Rénovation</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Suivre budget & délais
                      </p>
                    </Card>
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-2">
                        <TrendingUp className="text-blue-500" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">Suivi Sortie</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Optimiser le timing de vente
                      </p>
                    </Card>
                    <Card className="text-center">
                      <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-2">
                        <Brain className="text-purple-500" size={20} />
                      </div>
                      <h4 className="font-medium text-sm">AI Intelligence</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Score & recommend
                      </p>
                    </Card>
                  </div>
                </SubSection>

                <SubSection title="Data Flow">
                  <Card>
                    <ol className="space-y-3">
                      {[
                        'External data imported via CSV or manual entry',
                        'Records reviewed and approved in Data Ingestion',
                        'Comparables feed into deal pricing analysis',
                        'Deal Analyzer calculates all economics',
                        'AI synthesizes signals into actionable scores',
                        'Results flow into reporting and decision tools',
                      ].map((step, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                            {idx + 1}
                          </div>
                          <span className="text-sm">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </Card>
                </SubSection>
              </div>
            </Section>

            {/* ============== INVESTMENT WORKFLOW ============== */}
            <Section id="workflow" title="Investment Workflow" icon={GitBranch}>
              <ProseBlock>
                <p className="mb-4">
                  Follow this step-by-step process from deal sourcing to post-sale review. 
                  Each step includes the tools you need and the decisions you'll make.
                </p>
              </ProseBlock>

              <div className="space-y-4">
                {investmentWorkflow.steps.map((step, idx) => (
                  <Card key={step.id} id={step.id} className="relative">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold flex-shrink-0">
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{step.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {step.description}
                        </p>
                        
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                              Actions
                            </h4>
                            <ul className="space-y-1">
                              {step.actions.map((action, i) => (
                                <li key={i} className="text-sm flex items-start gap-2">
                                  <CircleDot className="text-primary mt-1 flex-shrink-0" size={10} />
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                              Tools Used
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {step.tools.map((tool, i) => (
                                <span 
                                  key={i}
                                  className="px-2 py-0.5 text-xs bg-muted rounded"
                                >
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {step.nextStep && (
                      <div className="absolute -bottom-4 left-[18px] w-0.5 h-4 bg-border" />
                    )}
                  </Card>
                ))}
              </div>
            </Section>

            {/* ============== MODULE DEEP DIVES ============== */}
            <Section id="modules" title="Module Deep Dives" icon={BookOpen}>
              <div className="space-y-6">
                {Object.values(moduleDeepDives).map((module) => (
                  <Card key={module.id} id={module.id}>
                    <h3 className="font-semibold text-lg mb-2">{module.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{module.purpose}</p>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          Inputs
                        </h4>
                        <ul className="space-y-1">
                          {module.inputs.map((input, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <ChevronRight className="text-muted-foreground mt-0.5 flex-shrink-0" size={14} />
                              <span>{input}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          Outputs
                        </h4>
                        <ul className="space-y-1">
                          {module.outputs.map((output, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <ChevronRight className="text-primary mt-0.5 flex-shrink-0" size={14} />
                              <span>{output}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                        Decisions Supported
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {module.decisionsSupported.map((decision, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 text-xs bg-primary/5 text-primary rounded border border-primary/20"
                          >
                            {decision}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {/* Sample annotated screenshot */}
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">
                  Visual Guide: Deal Detail Page
                </h3>
                <AnnotatedScreenshot
                  placeholderTitle={pageAnnotations.dealDetail.placeholderTitle}
                  annotations={pageAnnotations.dealDetail.annotations}
                />
              </div>
            </Section>

            {/* ============== KEY CONCEPTS ============== */}
            <Section id="concepts" title="Key Concepts" icon={Lightbulb}>
              <div className="grid grid-cols-2 gap-4">
                {Object.values(keyConcepts).map((concept) => (
                  <Card key={concept.id} id={concept.id}>
                    <h3 className="font-semibold">{concept.title}</h3>
                    <p className="text-sm text-primary font-medium mt-1">
                      {concept.shortDescription}
                    </p>
                    <div className="mt-3 text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                      {concept.explanation.split('\n\n').slice(0, 2).join('\n\n')}
                    </div>
                    {concept.relatedConcepts && (
                      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Related:</span>
                        {concept.relatedConcepts.map((rel, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              const el = document.getElementById(rel);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                            className="text-xs text-primary hover:underline"
                          >
                            {rel.replace(/-/g, ' ')}
                          </button>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Section>

            {/* ============== DECISION FRAMEWORKS ============== */}
            <Section id="decisions" title="Decision Frameworks" icon={Target}>
              <ProseBlock>
                <p className="mb-4">
                  Use these frameworks to make consistent, data-driven investment decisions.
                </p>
              </ProseBlock>

              <div className="space-y-4">
                {Object.values(decisionFrameworks).map((framework) => (
                  <Card key={framework.id} id={framework.id}>
                    <h3 className="font-semibold text-lg mb-3">{framework.title}</h3>
                    <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed prose prose-sm max-w-none">
                      {framework.content.split('\n').map((line, i) => {
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return (
                            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
                              {line.replace(/\*\*/g, '')}
                            </h4>
                          );
                        }
                        if (line.startsWith('- ')) {
                          return (
                            <div key={i} className="flex items-start gap-2 ml-2">
                              <span className="text-primary">•</span>
                              <span>{line.replace('- ', '')}</span>
                            </div>
                          );
                        }
                        return <p key={i}>{line}</p>;
                      })}
                    </div>
                  </Card>
                ))}
              </div>
            </Section>

            {/* ============== PAGE REFERENCE ============== */}
            <Section id="pages" title="Page Reference" icon={FileText}>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(pageExplanations).map(([key, page]) => {
                  const icons = {
                    executiveOverview: Home,
                    dealPipeline: Briefcase,
                    dealDetail: FileText,
                    quickAnalyzer: Calculator,
                    renovationControl: Wrench,
                    marketComparables: Building2,
                    exitTracker: TrendingUp,
                    partnerScorecard: Users,
                    aiIntelligence: Brain,
                    dataIngestion: Database,
                  };
                  const Icon = icons[key] || FileText;
                  
                  const routes = {
                    executiveOverview: '/',
                    dealPipeline: '/pipeline',
                    dealDetail: '/pipeline',
                    quickAnalyzer: '/analyzer',
                    renovationControl: '/renovation',
                    marketComparables: '/comparables',
                    exitTracker: '/exit',
                    partnerScorecard: '/partners',
                    aiIntelligence: '/intelligence',
                    dataIngestion: '/data-import',
                  };

                  return (
                    <Card key={key} id={page.id}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="text-primary" size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold">{page.title}</h3>
                            <Link 
                              to={routes[key]}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Open page <ExternalLink size={10} />
                            </Link>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{page.what}</p>
                          
                          {page.metrics && (
                            <div className="mt-3">
                              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                                Key Metrics
                              </h4>
                              <div className="flex flex-wrap gap-1.5">
                                {page.metrics.slice(0, 4).map((m, i) => (
                                  <span 
                                    key={i}
                                    className="px-1.5 py-0.5 text-xs bg-muted rounded"
                                    title={m.description}
                                  >
                                    {m.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </Section>

          </main>
        </div>
      </div>
    </div>
  );
};

export default Help;
